import InstagramGradientIcon from "@/assets/7086FF_instagram_gradient.svg?react";
import TikTokGradientIcon from "@/assets/7086FF_tiktok_gradient.svg?react";
import CaretIcon from "@/assets/colorable/caret.svg?react";
import InviteUserIcon from "@/assets/colorable/invite_user.svg?react";
import Button from "@/components/Common/Button";
import ChatTab from "@/components/NavBar/ChatTab";
import NavbarAgencyProfile from "@/components/NavBar/NavbarAgencyProfile";
import NavbarCompanyProfile from "@/components/NavBar/NavbarCompanyProfile";
import NavbarIntercomToggle from "@/components/NavBar/NavbarIntercomToggle";
import NavbarLanguageSelector from "@/components/NavBar/NavbarLanguageSelector";
import NavButton from "@/components/NavBar/NavButton";
import MessageCountIndicator from "@/components/NavBar/NotificationBadge";
import NotificationPanel from "@/components/NavBar/NotificationPanel";
import Notifications from "@/components/NavBar/Notifications";
import SideDrawer from "@/components/NavBar/SideDrawer";
import {useLogout} from "@/hooks/useLogout";
import {getPackageIcon, packages} from "@/hooks/usePackages";
import usePlanFlow from "@/hooks/usePlanFlow";
import {
  useAgencyId,
  useIsAdmin,
  useIsAgency,
  useSelectedAgency,
  useSelectedCompanyReactive,
} from "@/hooks/useUserHooks";
import clsxm from "@/lib/clsxm";
import {Can} from "@/permissions";
import {
  editCompanyMemberModalAtom,
  hasJustRegisteredAtom,
  hasStartedCampaignCreationAtom,
  isAgencyInviteModalOpenAtom,
  multiAccountInviteModalAtom,
} from "@/store/jotaiAtoms";
import useNotificationStore from "@/store/notification";
import useRegistrationStore from "@/store/registration";
import type {TPackageName} from "@/store/types";
import useUserStore from "@/store/user";
import {AnimatePresence, motion} from "framer-motion";
import {useAtomValue, useSetAtom} from "jotai";
import findKey from "lodash/findKey";
import {useCallback, useMemo, useRef, useState, type FC} from "react";
import {useTranslation} from "react-i18next";
import {useLocation, useNavigate} from "react-router";
import {useClickAway, useDebounce, useHoverDirty, useMedia, useToggle} from "react-use";

const NavbarCompany: FC = () => {
  const {t} = useTranslation(["navigation", "common"]);
  const setInviteModal = useSetAtom(multiAccountInviteModalAtom);
  const setAgencyInviteModal = useSetAtom(isAgencyInviteModalOpenAtom);
  const setEditCompanyMemberModal = useSetAtom(editCompanyMemberModalAtom);
  const hasJustRegistered = useAtomValue(hasJustRegisteredAtom);
  const hasStartedCampaignCreation = useAtomValue(hasStartedCampaignCreationAtom);
  const isAgency = useIsAgency();
  const agencyId = useAgencyId();
  const agency = useSelectedAgency();
  const isAdmin = useIsAdmin();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const logout = useLogout();
  const navigate = useNavigate();
  const isMobileView = useMedia("(max-width: 1160px)");
  const selectedCompany = useSelectedCompanyReactive();
  const setRegistrationValue = useRegistrationStore(state => state.setValue);
  const toggleNotificationsOpen = useNotificationStore(state => state.toggleOpen);
  const isNotificationPanelOpen = useNotificationStore(state => state.isOpen);
  const user = useUserStore(state => state.user);
  const [isDrawerOpen, toggleDrawerOpen] = useToggle(false);
  const subscription = useUserStore(state => state.user.package_subscription);
  const [isChatOpen, setChatOpen] = useState(false);
  const flowState = usePlanFlow();
  const plan = useMemo(() => findKey(packages, {type: subscription?.name}) as TPackageName | undefined, [subscription]);
  const location = useLocation();
  const messagesRef = useRef<HTMLDivElement>(null!);
  const isHoveringOnMessages = useHoverDirty(messagesRef);
  useDebounce(() => setChatOpen(isHoveringOnMessages), isHoveringOnMessages ? 300 : 200, [isHoveringOnMessages]);

  const onClickAway = useCallback(() => {
    toggleNotificationsOpen(false);
    toggleDrawerOpen(false);
  }, [toggleDrawerOpen, toggleNotificationsOpen]);

  const onInviteButtonClick = useCallback(() => {
    if (!selectedCompany) {
      setAgencyInviteModal(true);
    } else {
      setInviteModal({
        isVisible: true,
        isViewingOwnCompany: true,
      });
    }
  }, [selectedCompany, setAgencyInviteModal, setInviteModal]);

  useClickAway(wrapperRef, onClickAway);

  const welcomeBackButton = useMemo(
    () => <NavButton to="/welcome/" text={t(flowState === "NO_PLAN_NO_CAMPAIGN" ? "start_here" : "welcome_back")} />,
    [t],
  );

  const dashboardButton = useMemo(
    () => (
      <NavButton
        to={
          isAgency
            ? selectedCompany
              ? `/agency/${agencyId}/subbrand/${selectedCompany.id}/dashboard/`
              : `/agency/${agencyId}/dashboard/`
            : `/company/${selectedCompany?.id}/dashboard/`
        }
        text={t(
          isAgency && !selectedCompany
            ? agency?.large_brand
              ? "large_brand_dashboard"
              : "agency_dashboard"
            : "dashboard",
        )}
      />
    ),
    [t, isAgency, agencyId, selectedCompany],
  );

  const customers = useMemo(
    () => <NavButton to={`/agency/${agencyId}/customers/`} text={t(agency?.large_brand ? "companies" : "customers")} />,
    [t, agencyId],
  );

  const influencersButton = useMemo(() => <NavButton to="/influencers-list/" text={t("influencers")} />, [t]);

  const campaignsButton = useMemo(() => <NavButton to="/campaigns/" text={t("campaigns")} />, [t]);

  const agenciesButton = useMemo(() => <NavButton to="/admins/customers/" text={t("customers")} />, [t]);

  const dashboardButtonsToShow = useMemo(() => {
    if (isAgency || hasStartedCampaignCreation) return dashboardButton;

    if (flowState === "NO_PLAN_NO_CAMPAIGN" || flowState === "HAS_PLAN_NO_CAMPAIGN") return welcomeBackButton;

    return dashboardButton;
  }, [isAgency, flowState, welcomeBackButton, dashboardButton, hasStartedCampaignCreation]);

  const unseenChatCount = useNotificationStore(state => state.notificationCounts.chat.unread);

  return isMobileView ? (
    <div ref={wrapperRef}>
      {(!hasJustRegistered || isAgency) && (
        <div className="flex gap-1">
          <NavbarLanguageSelector />
          <SideDrawer isOpen={isDrawerOpen} toggleOpen={toggleDrawerOpen}>
            {dashboardButtonsToShow}
            {isAdmin && agenciesButton}
            {isAgency && !selectedCompany && customers}
            {influencersButton}
            {campaignsButton}
            {!!selectedCompany && (
              <NavButton
                to={
                  isAgency ? `/agency/${agencyId}/chat/${selectedCompany.id}/` : `/company/${selectedCompany.id}/chat/`
                }
                text={t("messages")}
              />
            )}
            {isAdmin && (
              <>
                <NavButton
                  text={t("create_new_multi_account_user")}
                  to="/admins/agency/create/"
                  className="whitespace-normal"
                />
                <NavButton text={t("combine_accounts")} to="/admins/accounts/combine/" className="whitespace-normal" />
              </>
            )}
            <div className="bg-neutral-grey-light h-[1px] w-full" />
            {!!selectedCompany && (
              <NavButton
                text={t("notifications")}
                onClick={() => {
                  toggleDrawerOpen(false);
                  toggleNotificationsOpen(true);
                }}
              />
            )}
            <NavButton text={plan ? t("our_plans") : t("join_now_drawer")} to="/plans/" />
            {user.has_multi_account_users_access && (
              <Can I="create" a="CompanyMemberInvitation">
                <NavButton text={t("invite")} onClick={onInviteButtonClick} />
              </Can>
            )}
            <div className="bg-neutral-grey-light h-[1px] w-full" />
            {!!selectedCompany && <NavButton text={t("company_profile")} to={`/company/${selectedCompany?.id}/`} />}
            <NavButton
              text={t("your_profile")}
              onClick={() => {
                setRegistrationValue("isEmailPrefilled", true);
                setRegistrationValue("email", user.email ?? "");
                setRegistrationValue("firstName", user.first_name ?? "");
                setRegistrationValue("lastName", user.last_name ?? "");

                setEditCompanyMemberModal({
                  isVisible: true,
                  isRegistering: false,
                });
              }}
            />
            <Can I="read" a="PaymentHistory">
              <NavButton text={t("payment_history")} to="/invoices" />
            </Can>
            <NavButton text={t("logout")} onClick={() => logout()} />
          </SideDrawer>
        </div>
      )}
      {isNotificationPanelOpen && <NotificationPanel />}
    </div>
  ) : (
    <div className={clsxm("flex h-full items-center gap-5", isAgency && "gap-3.5")}>
      {!hasJustRegistered && (
        <>
          <div className={clsxm("flex flex-row items-center gap-5 xl:gap-7 2xl:gap-10", isAgency && "gap-3.5")}>
            {dashboardButtonsToShow}
            {isAdmin && agenciesButton}
            {isAgency && !selectedCompany && customers}
            {influencersButton}
            {campaignsButton}
            {!!selectedCompany && (
              <div ref={messagesRef} className="relative flex h-20 items-center">
                <div
                  className={`relative flex items-center rounded-full transition-transform duration-150 ${!location.pathname.includes("/chat") && "hover:scale-[1.02]"}`}>
                  <NavButton
                    to={
                      isAgency
                        ? `/agency/${agencyId}/chat/${selectedCompany.id}/`
                        : `/company/${selectedCompany.id}/chat/`
                    }
                    text={t("messages")}
                    className="hover:scale-none"
                  />
                  <MessageCountIndicator
                    isFilled
                    count={unseenChatCount}
                    className="border-neutral-white-dark absolute -left-3.5 -translate-y-2 border-2"
                  />
                </div>
                <div className="absolute top-20 left-1/2 w-[33vw] -translate-x-1/2">
                  <AnimatePresence>
                    {isChatOpen && (
                      <motion.div
                        initial={{opacity: 0}}
                        animate={{opacity: 1}}
                        exit={{opacity: 0}}
                        transition={{duration: 0.2}}>
                        <div className="flex justify-center">
                          <CaretIcon className="text-neutral-grey-light-dark absolute -top-4 -z-1 h-5 w-8" />
                          <div className="border-neutral-white-dark overflow-auto rounded-md border bg-white shadow-2xl md:shadow-md">
                            <ChatTab />
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            )}
            {user.has_multi_account_users_access && (
              <Can I="create" a="CompanyMemberInvitation">
                <Button onClick={onInviteButtonClick} buttonStyle="Blue" className="h-10 gap-2 px-5">
                  <InviteUserIcon className="size-5 shrink-0" />
                  {t("invite")}
                </Button>
              </Can>
            )}
          </div>
          <div className="flex flex-row gap-5">
            {!!selectedCompany && <Notifications className="flex flex-col" />}
            {!plan ? (
              isAgency ? null : (
                <Can I="update" a="CompanyPlan">
                  <Button
                    buttonStyle="Blue"
                    onClick={() => {
                      navigate("/plans/");
                    }}
                    className="h-10 w-full self-center px-4">
                    {t("join_now")}
                  </Button>
                </Can>
              )
            ) : (
              <Button
                onClick={() => navigate("/plans/")}
                className="border-primary-light-solid bg-neutral-grey-lightest h-10 w-full gap-2 self-center border px-4 text-white">
                {getPackageIcon(plan, "size-5")}
                <span className="bg-linear-(--primary-dark-gradient) bg-clip-text text-transparent">
                  {t(`common:${packages[plan].translationKey}`)}
                </span>
                <div className="flex items-center gap-1">
                  {subscription?.instagram_campaigns && <InstagramGradientIcon className="h-3.5 w-auto shrink-0" />}
                  {subscription?.tiktok_campaigns && <TikTokGradientIcon className="h-3.5 w-auto shrink-0" />}
                </div>
                {!!user.payment_failed_invoices?.length && (
                  <div className="border-danger-rubicund bg-danger-light-red text-danger-medium-red flex max-h-8 items-center rounded-md border p-2 text-[10px] leading-3 font-bold uppercase">
                    {t("common:payment_failed")}
                  </div>
                )}
              </Button>
            )}
          </div>
        </>
      )}
      <NavbarLanguageSelector />
      <NavbarIntercomToggle />
      {!hasJustRegistered && (isAgency ? <NavbarAgencyProfile /> : <NavbarCompanyProfile />)}
    </div>
  );
};

export default NavbarCompany;
